import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAllMandates, useSepaBatches } from "@/lib/sepa";

const fmt = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });
const dfmt = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

export default function SepaPage() {
  const { data: mandates = [], isLoading } = useAllMandates();
  const { data: batches = [] } = useSepaBatches();
  const [collectionDate, setCollectionDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 6); return d.toISOString().slice(0, 10);
  });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  async function generate() {
    setRunning(true);
    setResult(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const res = await fetch("/api/documents?action=sepa", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sess.session?.access_token ?? ""}`,
        },
        body: JSON.stringify({ collection_date: collectionDate }),
      });
      if (!res.ok) {
        const txt = await res.text();
        let err = txt;
        try { err = JSON.parse(txt).error; } catch {/*ignore*/}
        setResult(`Fehler: ${err}`);
        return;
      }
      const xml = await res.text();
      const blob = new Blob([xml], { type: "application/xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const cd = res.headers.get("content-disposition") || "";
      const fname = cd.match(/filename="([^"]+)"/)?.[1] ?? `sepa-${collectionDate}.xml`;
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      setResult(`✓ ${fname} heruntergeladen. Bitte ins Online-Banking hochladen.`);
    } catch (e) {
      setResult(`Fehler: ${(e as Error).message}`);
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold text-hof-900">SEPA</h1>

      <section className="card space-y-3">
        <h2 className="font-semibold text-hof-800">Neuer SEPA-Lauf</h2>
        <p className="text-sm text-hof-600">
          Sammelt alle offenen Rechnungen mit aktivem SEPA-Mandat und erzeugt eine pain.008.001.02 XML-Datei
          zum manuellen Upload ins Online-Banking.
        </p>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="label">Einzugsdatum</label>
            <input type="date" className="input" value={collectionDate}
                   onChange={(e) => setCollectionDate(e.target.value)} />
          </div>
          <button onClick={generate} disabled={running} className="btn-primary">
            {running ? "Erzeuge…" : "▶ Lauf erzeugen & XML herunterladen"}
          </button>
        </div>
        {result && (
          <div className={`rounded-xl px-3 py-2 text-sm ${result.startsWith("✓") ? "bg-wiese-50 border border-wiese-200 text-wiese-800" : "bg-red-50 border border-red-200 text-red-800"}`}>
            {result}
          </div>
        )}
      </section>

      <section className="card">
        <h2 className="font-semibold text-hof-800 mb-3">Vergangene Läufe</h2>
        {batches.length === 0 ? (
          <p className="text-center text-hof-600 py-4">Noch keine Läufe.</p>
        ) : (
          <div className="space-y-2">
            {batches.map((b) => (
              <div key={b.id} className="flex items-center justify-between py-2 border-b border-hof-100 last:border-0">
                <div>
                  <div className="font-mono text-sm">{b.batch_ref}</div>
                  <div className="text-xs text-hof-500">
                    Einzug {dfmt.format(new Date(b.collection_date))} · erzeugt {dfmt.format(new Date(b.created_at))}
                  </div>
                </div>
                <div className="text-right">
                  <span className="pill bg-hof-100 text-hof-800">{b.status}</span>
                  <div className="font-mono text-lg mt-1">{fmt.format(b.total_cents / 100)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="card">
        <h2 className="font-semibold text-hof-800 mb-3">Aktive Mandate</h2>
        {isLoading ? <p>Lade…</p>
          : mandates.length === 0 ? <p className="text-center text-hof-600 py-4">Keine Mandate.</p>
          : (
          <table className="w-full text-left">
            <thead className="text-sm text-hof-600">
              <tr>
                <th className="py-2">Mandatsref</th>
                <th className="py-2">Kunde</th>
                <th className="py-2">Kontoinhaber</th>
                <th className="py-2">IBAN-Endung</th>
                <th className="py-2">Erteilt</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {mandates.map((m) => (
                <tr key={m.id} className="border-t border-hof-100">
                  <td className="py-2 font-mono text-sm">{m.mandate_ref}</td>
                  <td className="py-2">{m.customer?.display_name ?? "—"}</td>
                  <td className="py-2">{m.account_holder}</td>
                  <td className="py-2 font-mono">•••• {m.iban_last4}</td>
                  <td className="py-2 text-sm">{dfmt.format(new Date(m.signed_at))}</td>
                  <td className="py-2">
                    {m.revoked_at
                      ? <span className="pill-danger">Widerrufen</span>
                      : <span className="pill-success">Aktiv</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
